import is from '@sindresorhus/is';
import { AnimatePresence } from 'framer-motion';
import type { FunctionComponent } from 'react';
import { Fragment, memo, useEffect, useState } from 'react';

import type { JobRole } from '~/shared/data-contract/job-role';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { ShakeAnimation } from '~/ui/animations/shake/ShakeAnimation';
import { useShakeOnClickOutside } from '~/ui/animations/shake/use-shake-on-click-outside';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent } from '~/ui/core/tile/Tile';
import { TileFooter } from '~/ui/core/tile/TileFooter';

import { AgentChangeGlimmer } from '../../chat/AgentChangeGlimmer';
import type { UrlTileHost } from '../../url-tiles';
import { Backdrop, ContentContainer } from '../components/Common';
import { ErrorContent } from '../components/ErrorContent';
import { isMainGenerationInProgress } from '../hooks/use-is-generating';
import { JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../url-tile-payloads';
import { DetailsContent } from './DetailsContent';
import { LoadingContent } from './LoadingContent';
import { useGetActiveJobProfileVersion } from './use-get-active-job-profile-version';

export type AvailableJobItems = {
    readonly architectureId: string;
    readonly clusterId: string;
    readonly roles: readonly JobRole[];
};

type DetailsProps = {
    readonly architectureId: string;
    readonly host?: UrlTileHost;
    readonly isArchived?: boolean;
    readonly isCommentsOpen?: boolean;
    readonly isOpen: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly mode: 'edit' | 'view';
    readonly onActiveLanguageChange?: (language: LanguageCodeForJSA) => void;
    readonly onClose?: () => void;
    readonly onToggleComments?: () => void;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly version?: number;
};

const DetailsImpl: FunctionComponent<DetailsProps> = ({
    architectureId,
    host,
    isArchived,
    isCommentsOpen = false,
    isOpen = true,
    itemId,
    itemType = 'job-role',
    mode,
    onActiveLanguageChange,
    onClose,
    onToggleComments,
    preferredLanguage,
    version,
}) => {
    const { activeVersion, error, isLoading, item, setActiveVersion } = useGetActiveJobProfileVersion(
        itemId,
        itemType,
        architectureId,
        is.number(version) ? version.toString() : undefined,
    );
    const [isEditing, setIsEditing] = useState(mode === 'edit');
    const { isShaking, shake } = useShakeOnClickOutside();
    const isGenerating = error === null && isMainGenerationInProgress(item?.profileGenerationInfo);

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        setIsEditing(mode === 'edit');
    }, [mode, itemId]);

    if (isLoading || isGenerating) {
        return (
            <Tile className="[&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:h-full">
                <ScrollArea scrollFade="vertical" scrollFadeSize="var(--spacing-4xl)">
                    <TileContent>
                        <ContentContainer>
                            <LoadingContent
                                tileKey={`${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${architectureId}.${itemId}`}
                                onClose={onClose}
                            />
                        </ContentContainer>
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    if (error !== null || is.nullOrUndefined(item)) {
        return (
            <Tile className="[&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:h-full">
                <ScrollArea scrollFade="vertical" scrollFadeSize="var(--spacing-4xl)">
                    <TileContent>
                        <ContentContainer>
                            <ErrorContent error={error} onClose={onClose} />
                        </ContentContainer>
                    </TileContent>
                </ScrollArea>
            </Tile>
        );
    }

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <Fragment>
                    {isEditing && <Backdrop onClick={shake} />}
                    <ShakeAnimation isShaking={isShaking}>
                        <Tile
                            className="relative [&_[data-radix-scroll-area-viewport]>div]:block! [&_[data-radix-scroll-area-viewport]>div]:h-full"
                            data-testid="job-profile-details"
                        >
                            <AgentChangeGlimmer className="rounded-xxxl" entityId={isEditing ? undefined : itemId} />
                            <ScrollArea scrollFade="vertical" scrollFadeSize="var(--spacing-4xl)">
                                <div className="flex min-h-full w-full items-start">
                                    <div
                                        className="flex min-w-0 flex-1 self-stretch flex-col"
                                        data-testid="job-profile-body"
                                    >
                                        <TileContent>
                                            <ContentContainer>
                                                {is.undefined(item.jobProfile) === false && (
                                                    <DetailsContent
                                                        key={item.id}
                                                        architectureId={architectureId}
                                                        host={host}
                                                        isArchived={isArchived}
                                                        isCommentsOpen={isCommentsOpen}
                                                        isEditing={isEditing}
                                                        item={item}
                                                        itemId={item.id}
                                                        itemType={itemType}
                                                        onActiveLanguageChange={onActiveLanguageChange}
                                                        parentRoleId={
                                                            itemType === 'job-level' ? item.parentId : undefined
                                                        }
                                                        permissions={item.actions}
                                                        preferredLanguage={preferredLanguage}
                                                        profile={item.jobProfile}
                                                        selectedVersion={activeVersion}
                                                        setActiveVersion={setActiveVersion}
                                                        setIsEditing={setIsEditing}
                                                        onClose={onClose}
                                                        onToggleComments={onToggleComments}
                                                    />
                                                )}
                                                <div className="mt-auto ">
                                                    <TileFooter />
                                                </div>
                                            </ContentContainer>
                                        </TileContent>
                                    </div>
                                </div>
                            </ScrollArea>
                        </Tile>
                    </ShakeAnimation>
                </Fragment>
            )}
        </AnimatePresence>
    );
};

export const Details = memo(DetailsImpl);
